import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";

/**
 * Progress state interface for tracking multi-step operations
 * Use this type when implementing progress tracking in your components
 */
export interface ProgressState {
  current: number;
  total: number;
  currentItem: string;
}

interface ProgressDialogProps {
  open: boolean;
  title: string;
  message: string;
  current: number;
  total: number;
  itemName?: string; // e.g., "placement", "trip"
  currentItem?: string; // e.g., "N123AB", "Trip 5"
}

export const ProgressDialog: React.FC<ProgressDialogProps> = ({
  open,
  title,
  message,
  current,
  total,
  itemName = "item",
  currentItem,
}) => {
  // Calculate progress percentage
  const progress = total > 0 ? (current / total) * 100 : 0;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          minHeight: 200,
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography variant="h6">{title}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Typography variant="body1" color="text.secondary">
            {message}
          </Typography>

          {/* Progress bar */}
          <Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Progress text */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Processing {current} of {total} {itemName}
              {total > 1 ? "s" : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              {Math.round(progress)}%
            </Typography>
          </Stack>

          {/* Current item being processed */}
          {currentItem && (
            <Typography variant="body2" color="primary" sx={{ fontStyle: "italic" }}>
              Current: {currentItem}
            </Typography>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
